import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import invariant from 'tiny-invariant';

import { ONE_DAY } from '@air/utilities';

import { useClipTranscriptFetchQueueContext } from '~/providers/ClipTranscriptFetchQueueProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { MAX_TRANSCRIPT_RANGE_IN_MS } from '~/swr-hooks/clips/transcript/constants';
import { type ClipTranscriptCache, type TimeRange } from '~/swr-hooks/clips/transcript/types';
import {
  fetchClipTranscriptSegments,
  getClipTranscriptCacheForRange,
} from '~/swr-hooks/clips/transcript/utils/fetchClipTranscriptSegments';

export const getPrivateClipTranscriptSegmentsKey = (clipId: string) => ['PRIVATE_CLIP_TRANSCRIPT_SEGMENTS', { clipId }];

export const useGetPrivateClipTranscriptSegments = ({
  clipId,
  enabled = true,
  videoDurationMs,
}: {
  clipId: string;
  enabled?: boolean;
  videoDurationMs: number;
}) => {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useCurrentWorkspace();
  const [isFetching, setIsFetching] = useState(false);
  const key = useMemo(() => getPrivateClipTranscriptSegmentsKey(clipId), [clipId]);

  const { fetchQueue, queueRangeFetch } = useClipTranscriptFetchQueueContext();

  // biome-ignore lint/correctness/useExhaustiveDependencies(clipId): cancel all fetch queue when clipId changes
  useEffect(() => {
    fetchQueue.current?.cancelAll();
  }, [clipId, fetchQueue]);

  const fetchClipsSegments = useCallback(
    async ({ startTimeMs, endTimeMs }: TimeRange) => {
      await queryClient.cancelQueries({ queryKey: key });
      invariant(currentWorkspace, 'Current workspace should be defined');
      return fetchClipTranscriptSegments({
        clipId,
        workspaceId: currentWorkspace.id,
        startTimeMs,
        endTimeMs,
        videoDurationMs,
      });
    },
    [clipId, currentWorkspace, key, queryClient, videoDurationMs],
  );

  const fetchQueryData = useCallback(
    async ({ startTimeMs, endTimeMs }: TimeRange): Promise<ClipTranscriptCache> => {
      const queryData = queryClient.getQueryData<ClipTranscriptCache>(key);

      return getClipTranscriptCacheForRange({
        startTimeMs,
        endTimeMs,
        initialData: queryData?.data ?? [],
        fetchData: fetchClipsSegments,
        videoDurationMs,
      });
    },
    [fetchClipsSegments, key, queryClient, videoDurationMs],
  );

  const _fetchTranscriptForRange = useCallback(
    async ({ startTimeMs, endTimeMs }: TimeRange) => {
      setIsFetching(true);
      try {
        const newData = await fetchQueryData({ startTimeMs, endTimeMs });
        queryClient.setQueryData(key, newData);
        return newData;
      } finally {
        setIsFetching(false);
      }
    },
    [fetchQueryData, key, queryClient],
  );

  const fetchTranscriptForRange = useCallback(
    ({ startTimeMs, endTimeMs }: TimeRange) =>
      queueRangeFetch({
        clipId,
        startTimeMs,
        endTimeMs,
        fn: () => _fetchTranscriptForRange({ startTimeMs, endTimeMs }),
      }),
    [_fetchTranscriptForRange, clipId, queueRangeFetch],
  );

  const { data, error, isPending } = useQuery({
    queryKey: key,
    queryFn: () => fetchTranscriptForRange({ startTimeMs: 0, endTimeMs: MAX_TRANSCRIPT_RANGE_IN_MS }),
    staleTime: ONE_DAY,
    enabled: enabled && !!currentWorkspace,
  });

  return {
    key,
    data,
    isFetching,
    isInitialLoading: !data && !error && ((enabled && isPending) || isFetching),
    fetchTranscriptForRange,
  };
};
